/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiBase } from 'api/api';
import { ApiResponse } from 'api/interface/api';
import { AuthenticateResponse } from 'api/interface/authenticate';
import {
    BackofficeGetListOfUsersRequest,
    GetListOfUsersResponse,
    userListDropdownRequest,
    GetUserRequest,
    GetUserResponse,
    BackofficeUpdateUserRequest,
    UpdateUserResponse,
    BackofficeCreateUserRequest,
    BackofficeCreateUserResponse,
    CreateReceptionUserRequest,
    CreateReceptionUserResponse,
    GetPermissionResponse,
    NewUser1,
    ParkingSpotsResponse,
    HeatMapResponse,
    GetUserHeatMapRequest,
    GetUsersCursorResponse,
    SendNotificationRequest,
    SendNotificationResponse,
    UserDayDetailResponse,
    GetUserDayDetailRequest,
} from 'api/interface/user';
import { getCETTimestamp } from 'util/dateTimeConverter';
import { handleBlobReportResponse } from 'util/downloadReport';

const getListOfUsers = async (
    req: BackofficeGetListOfUsersRequest
): Promise<GetListOfUsersResponse> => {
    const sortCol = req.sortcol || 'Created Date';
    const sortOrder = req.sortorder || 'DESC';
    const res = await apiBase.get(
        `/v3/users?sort_col=${sortCol}&sort_order=${sortOrder}`,
        {
            params: req,
            paramsSerializer: (params) => {
                return new URLSearchParams(params).toString();
            },
        }
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            data: [],
            total: 0,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        data: res.data.data.map((item: NewUser1) => ({
            user: {
                id: item.user.id,
                name: item.user.name,
                username: item.user.username,
                email: item.user.email,
                status: item.user.status,
                user_note: item.user.user_note ?? '',
            },
            organisation: {
                id: item.organisation.id,
                name: item.organisation.name,
            },
        })),
        total: res.data.total,
    } as GetListOfUsersResponse;
};

const userListDropdown = async (
    req: userListDropdownRequest
): Promise<GetListOfUsersResponse> => {
    const sortCol = req.sortcol || 'Created Date';
    const sortOrder = req.sortorder || 'DESC';
    const res = await apiBase.get(
        `/v3/platform/users?sort_col=${sortCol}&sort_order=${sortOrder}`,
        {
            params: req,
            paramsSerializer: (params) => {
                return new URLSearchParams(params).toString();
            },
        }
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            data: [],
            total: 0,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        data: res.data.data.map((item: NewUser1) => ({
            user: {
                id: item.user.id,
                username: item.user.username,
            },
        })),
        total: res.data.total,
    } as GetListOfUsersResponse;
};

const getUser = async (req: GetUserRequest): Promise<GetUserResponse> => {
    const res = await apiBase.get(`/v3/users/${req.id}`);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: {
            user: {
                ...res.data.user,
                created_at: res.data.user.created_at
                    ? getCETTimestamp(res.data.user.created_at)
                    : null,
                updated_at: res.data.user.updated_at
                    ? getCETTimestamp(res.data.user.updated_at)
                    : null,
                last_login_web: res.data.user.last_login_web
                    ? getCETTimestamp(res.data.user.last_login_web)
                    : null,
                last_login_app: res.data.user.last_login_app
                    ? getCETTimestamp(res.data.user.last_login_app)
                    : null,
                start_date: res.data.user.start_date
                    ? getCETTimestamp(res.data.user.start_date)
                    : null,
                end_date: res.data.user.end_date
                    ? getCETTimestamp(res.data.user.end_date)
                    : null,
            },
            organisation: res.data.organisation,
            reception_details: res.data.reception,
            user_parking_lots_ids: res.data.user_address_ids,
            custom_guest_rules: res.data.custom_guest_rules,
            role_permissions: res.data.role_permissions,
            access_group_id: res.data.access_group_id,
            user_identity: res.data.identity,
            user_subordinates: res.data.subordinates,
        },
    } as GetUserResponse;
};

const getAvailablePermissions = async (): Promise<GetPermissionResponse> => {
    const res = await apiBase.get(`/v3/users/permissions`);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetPermissionResponse;
};

const updateUser = async (
    id: number,
    req: BackofficeUpdateUserRequest
): Promise<UpdateUserResponse> => {
    req.email = req.email.toLowerCase();

    const res = await apiBase.put(`/v3/users/${id}`, {
        ...req,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as UpdateUserResponse;
};

const createUser = async (
    req: BackofficeCreateUserRequest
): Promise<BackofficeCreateUserResponse> => {
    const res = await apiBase.post('/v3/users', {
        ...req,
    });

    if (res.status >= 300) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as BackofficeCreateUserResponse;
};

const sendPassword = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.put(`/v3/users/password/update/${id}`, null, {
        timeout: 15000,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const createReceptionUser = async (
    req: CreateReceptionUserRequest
): Promise<CreateReceptionUserResponse> => {
    const res = await apiBase.post('/v3/users', {
        ...req,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreateReceptionUserResponse;
};

const authenticateWithoutPassword = async (
    id: number
): Promise<AuthenticateResponse> => {
    const res = await apiBase.post(`/v3/users/as/${id}`);
    if (res.status !== 200) {
        return {
            authenticated: false,
            token: '',
            organisation_id: 0,
            user_id: 0,
            username: '',
            role_permissions: {},
            backoffice_organisation: false,
            backoffice_organisation_id: 0,
            email: '',
            organisation_logo: '',
            tokenExpiry: 0,
        };
    }

    const { role_permissions, user_details } = res.data;
    return {
        authenticated: true,
        token: user_details.token,
        organisation_id: user_details.organisation_id,
        user_id: user_details.user_id,
        username: user_details.email,
        role_permissions,
        email: user_details.email,
        backoffice_organisation: user_details.backoffice_organisation,
        backoffice_organisation_id: user_details.backoffice_organisation_id,
        organisation_logo: user_details.organisation_logo,
        tokenExpiry: user_details.TokenExpires,
    };
};

const parkingspotsOfMultipleParkinglots = async (
    req: number[]
): Promise<ParkingSpotsResponse> => {
    const res = await apiBase.get(`/parkinglot/spot-member/${req}`);

    if (res.status !== 200) {
        return {
            payload: [],
            error: 'bad request',
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data.parking_spots,
    } as ParkingSpotsResponse;
};

const takeOverOrganisationAsOparkoIt = async (
    organisation_id: number
): Promise<AuthenticateResponse> => {
    const res = await apiBase.post(
        `/v3/organisations/take-over/${organisation_id}`
    );
    if (res.status !== 200) {
        return {
            authenticated: false,
            token: '',
            organisation_id: 0,
            user_id: 0,
            username: '',
            role_permissions: {},
            backoffice_organisation: false,
            backoffice_organisation_id: 0,
            email: '',
            organisation_logo: '',
            tokenExpiry: 0,
        };
    }

    const { role_permissions, user_details } = res.data;
    return {
        authenticated: true,
        token: user_details.token,
        organisation_id: user_details.organisation_id,
        user_id: user_details.user_id,
        username: user_details.email,
        role_permissions,
        email: user_details.email,
        backoffice_organisation: user_details.backoffice_organisation,
        backoffice_organisation_id: user_details.backoffice_organisation_id,
        organisation_logo: user_details.organisation_logo,
        tokenExpiry: user_details.TokenExpires,
    };
};

const USER_REPORT_ROW_LIMIT =
    'Unable to generate report. The amount of rows exceeded 200,000 — please apply filters to minimize the amount.';

const generateReport = async (
    req: BackofficeGetListOfUsersRequest
): Promise<ApiResponse> => {
    const res = await apiBase.get(`/v3/users/generate-report`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
        responseType: 'blob',
        timeout: 0,
    });

    const result = await handleBlobReportResponse(res, {
        non200Error: USER_REPORT_ROW_LIMIT,
    });

    return {
        error: result.error,
        statusCode: result.statusCode,
    } as ApiResponse;
};

const getUserShiftActivityHeatmap = async (
    req?: GetUserHeatMapRequest
): Promise<HeatMapResponse> => {
    const res = await apiBase.get('/v3/users/shift-activity/heatmap', {
        params: req,
        paramsSerializer: (params) => {
            const filtered = Object.fromEntries(
                Object.entries(params).filter(
                    ([, v]) => v !== undefined && v !== ''
                )
            );
            return new URLSearchParams(filtered).toString();
        },
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [],
                from: '',
                to: '',
                mode: 'overview',
                total_users: 0,
            },
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as HeatMapResponse;
};

const getUserList = async (req?: any): Promise<GetUsersCursorResponse> => {
    const res = await apiBase.get('/v3/users/filter', {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: { users: [], hasMore: false, nextCursor: 0 },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetUsersCursorResponse;
};

const sendNotifications = async (
    req: SendNotificationRequest
): Promise<SendNotificationResponse> => {
    const res = await apiBase.post('/v3/users/notification/send', req);

    if (res.status !== 200) {
        return {
            error: res.data?.error || 'bad request',
            statusCode: res.status,
            errorMsg: res.data?.msg || '',
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

const getUserDayDetail = async (
    req: GetUserDayDetailRequest
): Promise<UserDayDetailResponse> => {
    const res = await apiBase.get('/v3/users/shift-activity/day-detail', {
        params: req,
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                userId: '',
                userName: '',
                day: '',
                totalEvents: 0,
                shiftDurationMin: 0,
                hourlyStats: [],
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    };
};

export const backofficeUserApi = {
    getListOfUsers,
    userListDropdown,
    getUser,
    updateUser,
    createUser,
    createReceptionUser,
    sendPassword,
    authenticateWithoutPassword,
    getAvailablePermissions,
    parkingspotsOfMultipleParkinglots,
    takeOverOrganisationAsOparkoIt,
    generateReport,
    getUserShiftActivityHeatmap,
    getUserList,
    sendNotifications,
    getUserDayDetail,
};
